import { css, DefaultTheme } from 'styled-components';
import { Breakpoints, Colors } from '../types';

/**
 * Helper functions for using theme in styled components
 */

/**
 * A shorthand for accessing spacings from theme
 *
 * @example
 * const Container = styled.div`
 *   padding: ${spacing(2)} ${spacing(1)};
 * `
 * @param index
 * @returns {function({theme: *}): *}
 */
export const spacing = (index: number) => ({ theme }: { theme: DefaultTheme }): string | number => theme.spacing[index];

/**
 * A shorthand for accessing shades from theme
 *
 * @example
 * const Container = styled.div`
 *   padding: ${shade(3)};
 * `
 * @param index
 * @returns {function({theme: *}): string}
 */
export const shade = (index: number) => ({ theme }: { theme: DefaultTheme }): string => theme.colors.shades[index];

/**
 * A shorthand for accessing colors from theme
 *
 * @example
 * const ErrorMessage = styled.div`
 *   color: ${color('error', 'default')};
 *   background-color: ${color('light')}
 * `;
 * @param name
 * @param variant (optional)
 * @returns {function({theme: *}): *}
 */
export const color = (name: keyof Colors, variant?: string | number) => ({ theme }: { theme: DefaultTheme }): string => (variant
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  ? theme.colors[name][variant]
  : theme.colors[name]
);

/**
 * A shorthand for building media queries using breakpoints from theme
 *
 * @example
 * const Container = styled.div`
 *   width: 300px;
 *   ${media('tablet')`
 *     width: 600px;
 *   `}
 * `;
 * @param breakpoint key of theme.breakpoints
 * @returns {function(...[*]): function({theme: *}): *}
 */
export const media =
  (breakpoint: keyof Breakpoints) => (first: TemplateStringsArray, ...args: any[]) => ({ theme }: { theme: DefaultTheme }) => css`
    @media ${theme.breakpoints[breakpoint]} {
      ${css(first, ...args)}
    }
`;
